<template>
  <div class="fav-chart-card">
    <div class="fav-apt-head">
      <span class="fav-apt-name">{{ apt.aptNm }}</span>
      <span class="fav-apt-gu">{{ guName(apt.sggCd) }}</span>
    </div>

    <div class="apt-chart-grid">
      <div
        class="chart-container"
        v-for="areaType in areaTypes"
        :key="areaType"
      >
        <div class="sub-title">
          {{ areaType === "small" ? "60㎡ 이하 (소형)" : "60㎡ 초과 (중대형)" }}
        </div>

        <div class="chart-box">
          <LineChart
            class="chart"
            v-if="chartData[areaType]"
            :data="chartData[areaType]"
            :options="chartOptions"
          />
        </div>

        <div class="summary">
          <div class="summary-item">
            <span class="s-label">총 거래</span>
            <span class="s-value">{{ getMoneyFormat(totalCount[areaType]) }}건</span>
          </div>
          <div class="summary-item">
            <span class="s-label">평균 금액</span>
            <span class="s-value accent">{{ avgAmount[areaType].toFixed(1) }}억</span>
          </div>
        </div>

        <div class="recent-trades">
          <div class="rt-head">최근 실거래</div>
          <div v-if="recentDeals[areaType].length === 0" class="rt-empty">
            거래 내역이 없습니다.
          </div>
          <div
            v-for="(item, index) in recentDeals[areaType]"
            :key="'recent-' + index"
            class="rt-row"
          >
            <span class="rt-date">
              {{ item.dealYear }}.{{ pad(item.dealMonth) }}.{{ pad(item.dealDay) }}
            </span>
            <span class="rt-area">
              {{ item.excluUseAr }}㎡
              <em>{{ toPyeong(item.excluUseAr) }}평</em>
            </span>
            <span class="rt-floor">{{ item.floor }}층</span>
            <span class="rt-amount">{{ toEok(item.dealAmount) }}억</span>
            <span
              class="rt-status"
              :class="item.cdealType === 'O' ? 'cancel' : 'normal'"
            >{{ item.cdealType === 'O' ? '해제' : '정상' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Line } from "vue-chartjs";
import UtilMixin from "@/common/mixins/UtilMixin";
import { AptFavorite, SeoulTradeStatRequest, AptTrade } from "../class";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ChartData,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler
);

const COUNT_COLOR = "#4a9fe0";
const COUNT_FILL = "rgba(74, 159, 224, 0.14)";
const AMOUNT_COLOR = "#e0a53a";
const TICK = "#8a9099";
const GRID = "rgba(128, 128, 128, 0.14)";

const GU_MAP: { [key: string]: string } = {
  "11110": "종로구", "11140": "중구", "11170": "용산구", "11200": "성동구",
  "11215": "광진구", "11230": "동대문구", "11260": "중랑구", "11290": "성북구",
  "11305": "강북구", "11320": "도봉구", "11350": "노원구", "11380": "은평구",
  "11410": "서대문구", "11440": "마포구", "11470": "양천구", "11500": "강서구",
  "11530": "구로구", "11545": "금천구", "11560": "영등포구", "11590": "동작구",
  "11620": "관악구", "11650": "서초구", "11680": "강남구", "11710": "송파구",
  "11740": "강동구",
};

type AreaType = "small" | "large";
const areaTypes: AreaType[] = ["small", "large"];
interface AreaTypeMap<T> {
  small: T;
  large: T;
}

export default defineComponent({
  name: "FavoriteChartItem",
  components: { LineChart: Line },
  props: {
    apt: {
      type: Object as () => AptFavorite,
      required: true,
    },
    period: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      areaTypes: ["small", "large"] as AreaType[],
      recentDeals: {
        small: [] as AptTrade[],
        large: [] as AptTrade[],
      } as AreaTypeMap<AptTrade[]>,
      chartData: {
        small: null as ChartData<"line"> | null,
        large: null as ChartData<"line"> | null,
      } as AreaTypeMap<ChartData<"line"> | null>,
      totalCount: { small: 0, large: 0 } as AreaTypeMap<number>,
      avgCount: { small: 0, large: 0 } as AreaTypeMap<number>,
      avgAmount: { small: 0, large: 0 } as AreaTypeMap<number>,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "top" as const,
            labels: { color: TICK, usePointStyle: true, boxWidth: 8, padding: 12 },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.dataset.label || "";
                const value = context.raw;
                if (label.includes("금액")) return `${label}: ${value}억`;
                return `${label}: ${Number(value).toLocaleString()}건`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: GRID },
            ticks: { color: TICK, maxRotation: 0, autoSkipPadding: 10 },
          },
          y1: {
            type: "linear",
            position: "left",
            grid: { color: GRID },
            ticks: { color: TICK },
          },
          y2: {
            type: "linear",
            position: "right",
            grid: { drawOnChartArea: false },
            ticks: {
              color: TICK,
              callback: function (value: any) {
                return `${value}억`;
              },
            },
          },
        },
      },
    };
  },
  mixins: [UtilMixin],
  async mounted() {
    await this.fetchTradeData();
  },
  watch: {
    period: "fetchTradeData",
  },
  methods: {
    guName(code: string): string {
      return GU_MAP[code] || "서울";
    },
    pad(n: number | string): string {
      return String(n).padStart(2, "0");
    },
    toPyeong(m2: number | string): string {
      const v = Number(m2);
      if (!v || isNaN(v)) return "-";
      return (v / 3.3058).toFixed(1);
    },
    toEok(amount: number): string {
      return (amount / 10000).toFixed(2);
    },
    async fetchTradeData() {
      const { sggCd, umdCd, aptNm } = this.apt;

      const groupBy = this.getGroupBy(this.period);
      const { startYmd, endYmd } = this.getDateRange(this.period);

      for (const areaType of areaTypes) {
        const request = {
          sggCd,
          umdCd,
          aptNm,
          minExcluUseAr: areaType === "small" ? 0 : 60,
          maxExcluUseAr: areaType === "small" ? 60 : 1000,
          startYmd,
          endYmd,
          groupBy,
        };
        const payload = new SeoulTradeStatRequest().bind(request);

        const res = await this.getApi().apt().getSeoulTradeStats(payload);

        const labels: string[] = [];
        const tradeCount: number[] = [];
        const tradeAmount: number[] = [];

        res.forEach((item: any) => {
          const { year, month, day, totalCount, totalAvgAmount } = item;
          labels.push(this.getLabel(year, month, day, groupBy));
          tradeCount.push(totalCount ?? 0);
          tradeAmount.push(totalAvgAmount ?? 0); // 이미 억 단위
        });

        this.chartData[areaType] = {
          labels: labels,
          datasets: [
            {
              label: "거래건수",
              data: tradeCount,
              borderColor: COUNT_COLOR,
              backgroundColor: COUNT_FILL,
              borderWidth: 2.5,
              fill: true,
              tension: 0.4,
              cubicInterpolationMode: "monotone",
              pointRadius: 0,
              pointHoverRadius: 5,
              yAxisID: "y1",
            },
            {
              label: "평균 계약금액",
              data: tradeAmount,
              borderColor: AMOUNT_COLOR,
              backgroundColor: AMOUNT_COLOR,
              borderWidth: 2.5,
              fill: false,
              tension: 0.4,
              cubicInterpolationMode: "monotone",
              pointRadius: 0,
              pointHoverRadius: 5,
              yAxisID: "y2",
            },
          ],
        };

        this.totalCount[areaType] = tradeCount.reduce((a, b) => a + b, 0);
        this.avgCount[areaType] = tradeCount.length
          ? this.totalCount[areaType] / tradeCount.length
          : 0;
        const amtVals = tradeAmount.filter((v) => v > 0);
        this.avgAmount[areaType] = amtVals.length
          ? amtVals.reduce((a, b) => a + b, 0) / amtVals.length
          : 0;

        this.fetchRecentDeals(startYmd, endYmd, areaType);
      }
    },
    async fetchRecentDeals(startYmd: string, endYmd: string, areaType: AreaType) {
      const { sggCd, umdCd, aptNm } = this.apt;

      const payload = {
        sggCd,
        umdCd,
        aptNm,
        minExcluUseAr: areaType === "small" ? 0 : 60,
        maxExcluUseAr: areaType === "small" ? 60 : 1000,
        dealDateFrom: startYmd,
        dealDateTo: endYmd,
        size: 12,
      };

      const res = await this.getApi().apt().getAptTrade(payload);
      this.recentDeals[areaType] = res.content || [];
    },
    getGroupBy(period: string): "DAY" | "MONTH" | "YEAR" {
      switch (period) {
        case "1W":
        case "1M":
          return "DAY";
        case "1Y":
          return "MONTH";
        case "5Y":
        case "10Y":
        default:
          return "YEAR";
      }
    },
    getLabel(year: number, month: number, day: number, groupBy: string): string {
      if (groupBy === "DAY") {
        return `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }
      if (groupBy === "MONTH") {
        return `${year}-${String(month).padStart(2, "0")}`;
      }
      return `${year}`;
    },
    getDateRange(period: string) {
      const now = new Date();
      let start = new Date();

      switch (period) {
        case "1W":
          start.setDate(now.getDate() - 7);
          break;
        case "1M":
          start.setMonth(now.getMonth() - 1);
          break;
        case "1Y":
          start.setFullYear(now.getFullYear() - 1);
          break;
        case "5Y":
          start.setFullYear(now.getFullYear() - 5);
          break;
        case "10Y":
        default:
          start.setFullYear(now.getFullYear() - 10);
      }

      const formatYmd = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = (date.getMonth() + 1).toString().padStart(2, "0");
        const dd = date.getDate().toString().padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      return {
        startYmd: formatYmd(start),
        endYmd: formatYmd(now),
      };
    },
  },
});
</script>
